import { escapeCsvCell } from '../../ui/data-table/components/table-export/csv-utils';
import type { AutomationRule } from '../worker-configuration/automation/worker-automation.types';
import { RULE_ACTION_SELECT_LABELS } from '../worker-configuration/automation/worker-automation.utils';
import { IDenialPaymentRow } from './denial-payments';
import { IDenialRemarkRow } from './denial-remark-rows';
import { IRejectionRow } from './rejection-rows';
import { coverageNote, IWorkerCoverage, matchedPhrase } from './worker-coverage';

/**
 * CSV export of the Denials & Rejections page (Jordan asked, 2026-10-07; John: "you have all the data loaded
 * already. Just toss it in an csv and export. No backend call needed."). Built from what the page holds, with
 * the columns the table shows — never from the table's cells, which are markup.
 *
 * Amounts are written as NUMBERS. `escapeCsvCell` guards text against spreadsheet formula injection by quoting
 * anything that starts with `-`, which would turn every reversal into text a spreadsheet cannot sum; a number
 * cannot be a formula, so numbers skip it. Text still goes through it.
 */

/** A number written with a fixed count of decimals — `12.50`, not `12.5` — and, being a number, never escaped. */
export interface ICsvFixed {
    fixed: number;
    decimals: number;
}

export type CsvValue = string | number | ICsvFixed | undefined;

export interface ICsvColumn<R> {
    header: string;
    value: (row: R) => CsvValue;
}

const cell = (value: CsvValue): string => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? String(value) : '';
    }
    if (value && typeof value === 'object') {
        return Number.isFinite(value.fixed) ? value.fixed.toFixed(value.decimals) : '';
    }
    return escapeCsvCell(value);
};

/** Cents, so a sum of signed amounts does not print as `1234.5600000000002`. */
const money = (value: number): number => Math.round(value * 100) / 100;

/**
 * The CSV text, with a byte-order mark: Excel reads a UTF-8 file without one as the local code page, and the
 * notes carry curly quotes and dashes.
 */
export const toCsv = <R>(rows: R[], columns: ICsvColumn<R>[]): string =>
    '﻿' +
    [columns.map(column => escapeCsvCell(column.header)).join(','), ...rows.map(row => columns.map(column => cell(column.value(row))).join(','))].join(
        '\r\n',
    );

const COVERAGE_TEXT: Record<IWorkerCoverage['status'], string> = {
    none: 'Not configured',
    partial: 'Partial',
    configured: 'Configured',
};

type Covered = { coverage?: IWorkerCoverage | null };

/** The rules cell as text. `undefined` coverage: rules not loaded; `null`: this row is not evaluated. */
const coverageColumns = <R extends Covered>(note: (row: R, coverage: IWorkerCoverage) => string): ICsvColumn<R>[] => [
    {
        header: 'Coverage (estimated)',
        value: row => (row.coverage === null ? 'Not evaluated' : row.coverage ? COVERAGE_TEXT[row.coverage.status] : ''),
    },
    {
        header: 'Action',
        value: row =>
            row.coverage?.rule ? RULE_ACTION_SELECT_LABELS[row.coverage.rule.action.type] || row.coverage.rule.action.type : '',
    },
    { header: 'Rule', value: row => row.coverage?.rule?.name },
    { header: 'Note', value: row => (row.coverage ? note(row, row.coverage) : '') },
];

/** `CO-45` — the code as the table shows it. */
const dashCode = (codes: string): string => codes.replace(':', '-');

export const denialsCsvColumns = (
    rules: AutomationRule[],
    noRule: string,
): Array<ICsvColumn<IDenialRemarkRow & { coverage?: IWorkerCoverage }>> => [
    { header: 'Code', value: row => dashCode(row.codes) },
    { header: 'RARC', value: row => row.remarkCodes.join(' ') },
    { header: 'Reason', value: row => row.reasonDescription },
    { header: 'Remark description', value: row => row.remarkDescription },
    { header: 'Adjustments', value: row => row.adjustments },
    { header: 'Adjustment amount', value: row => money(row.amount) },
    ...coverageColumns<IDenialRemarkRow & { coverage?: IWorkerCoverage }>((_row, coverage) =>
        coverageNote(coverage, rules, noRule),
    ),
];

/** The rejections table's note: the phrase that matched, or why nothing (fully) does. */
export const rejectionNote = (message: string, coverage: IWorkerCoverage, rules: AutomationRule[], noRule: string): string => {
    const phrase = coverage.rule ? matchedPhrase(message, coverage.rule) : undefined;
    return coverage.status === 'configured' && phrase
        ? `Matches “${phrase}”${coverage.also.length ? ` · also “${coverage.also[0].name}”` : ''}`
        : coverageNote(coverage, rules, noRule);
};

/**
 * `selectedType`: the status type the table was asked for. Rows carry their own type only in the "All status types"
 * view; in a single-type view every row is that type, which the table shows and the file must say too.
 */
export const rejectionsCsvColumns = (
    rules: AutomationRule[],
    noRule: string,
    selectedType?: string,
): Array<ICsvColumn<IRejectionRow & { coverage?: IWorkerCoverage | null }>> => [
    { header: 'Status code', value: row => row.statusCode },
    { header: 'Message', value: row => row.message },
    { header: 'Status type', value: row => row.statusType || (selectedType !== 'ALL' ? selectedType : undefined) },
    { header: 'Claims', value: row => row.claims },
    // Per message. Charges overlap ACROSS messages (one claim can carry several), so this column is not summable.
    { header: 'Charge (per message, overlaps)', value: row => money(row.charge) },
    ...coverageColumns<IRejectionRow & { coverage?: IWorkerCoverage | null }>((row, coverage) =>
        rejectionNote(row.message, coverage, rules, noRule),
    ),
];

/** The payments behind one denials row: every page of them, not only the one on screen. */
export const paymentsCsvColumns = (codeLabel: string): Array<ICsvColumn<IDenialPaymentRow>> => [
    { header: 'Payment date', value: row => row.paymentDate },
    { header: 'Received date', value: row => row.receivedDate },
    { header: 'Payer control #', value: row => row.payerControlNumber },
    { header: 'Status', value: row => row.status },
    { header: 'Procedures', value: row => row.allProcedureCodes.join(' ') },
    { header: 'RARC', value: row => row.remarkCodes.join(' ') },
    { header: 'Charge', value: row => money(row.charge) },
    { header: 'Allowed', value: row => money(row.allowed) },
    { header: 'Payer paid', value: row => money(row.payerPaid) },
    { header: 'Patient resp.', value: row => money(row.patientResponsibility) },
    { header: `${codeLabel} adj.`, value: row => money(row.matchedAmount) },
];

/**
 * `denials_2026-09-29_2026-10-05.csv`, `denials_CO-45_payments_2026-09-29_2026-10-05.csv`. `partial` marks a payment
 * list that stopped at the drill-down's row cap — the page says "more exist", and so must the file's name.
 */
export const denialsCsvFilename = (
    kind: 'denials' | 'rejections' | 'payments',
    firstDay: string,
    lastDay: string,
    code?: string,
    partial?: boolean,
): string =>
    kind === 'payments'
        ? `denials_${(code || '').replace(/[^A-Za-z0-9-]/g, '')}_payments_${firstDay}_${lastDay}${partial ? '_partial' : ''}.csv`
        : `${kind}_${firstDay}_${lastDay}.csv`;
